import { motion } from "framer-motion";
import { MapPin, Info, DollarSign, CircleCheck } from "lucide-react";
import { CONTACT, BOOKING_POLICY, SERVICE_CITIES } from "@/data/site";
import ZoneMap from "@/components/site/ZoneMap";

export default function ServiceArea() {
  return (
    <section id="service-area" data-testid="service-area-section" className="rs-section bg-white">
      <div className="rs-container">
        {/* Zone card */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
          className="relative rounded-3xl overflow-hidden border border-slate-200 bg-gradient-to-br from-[#0A192F] to-[#0F2544] p-8 md:p-12 text-white"
        >
          <div className="absolute -top-16 -right-16 h-64 w-64 rounded-full bg-[#F97316]/20 blur-3xl" />
          <div className="grid lg:grid-cols-12 gap-10 items-center relative">
            <div className="lg:col-span-6">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 px-3.5 py-1.5 text-xs uppercase tracking-widest text-[#F97316] font-semibold">
                <MapPin className="h-3.5 w-3.5" />
                Where we work
              </div>
              <h2 className="mt-5 font-display font-semibold text-3xl sm:text-4xl lg:text-5xl leading-tight">
                Based in{" "}
                <span className="text-[#F97316]">Salisbury, MD</span>.
                <br />
                Serving the Eastern Shore &amp; Delaware.
              </h2>
              <p className="mt-5 text-white/75 text-base md:text-lg leading-relaxed max-w-xl">
                We work throughout the Delmarva Peninsula. If your address is on the list below
                — or close to it — we&apos;re ready to book you in.
              </p>

              <div className="mt-6 flex items-start gap-3 rounded-xl bg-white/[0.06] border border-white/10 p-4">
                <Info className="h-5 w-5 text-[#F97316] flex-shrink-0 mt-0.5" />
                <p className="text-sm text-white/80">
                  Not sure if your address is in range? Call{" "}
                  <a
                    href={`tel:${CONTACT.phoneRaw}`}
                    className="text-[#F97316] font-semibold hover:underline"
                  >
                    {CONTACT.phone}
                  </a>{" "}
                  and we&apos;ll confirm right away.
                </p>
              </div>

              <div className="mt-8 grid gap-4">
                {Object.entries(SERVICE_CITIES).map(([region, cities]) => (
                  <CityGroup key={region} label={region} cities={cities} />
                ))}
              </div>
            </div>

            <div className="lg:col-span-6">
              <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl bg-[#0A192F]">
                <ZoneMap
                  className="w-full h-auto block"
                  data-testid="service-map-image"
                />
                <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-full bg-[#F97316] px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white shadow-lg">
                  <MapPin className="h-3 w-3" />
                  Service Zone
                </div>
                <div className="absolute bottom-3 right-3 inline-flex items-center gap-2 rounded-full bg-[#0A192F]/80 backdrop-blur px-3 py-1.5 text-[10px] font-semibold text-white/90 border border-white/10">
                  <span className="h-2 w-2 rounded-full bg-[#F97316]" />
                  Delmarva Coverage Area
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Minimum booking policy */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mt-16"
          data-testid="booking-policy-section"
        >
          <div className="max-w-2xl">
            <p className="rs-overline mb-4">Booking Policy</p>
            <h3 className="font-display font-semibold text-2xl sm:text-3xl lg:text-4xl text-[#0A192F] leading-tight">
              Fair, upfront minimums.
            </h3>
            <p className="mt-4 text-slate-600 text-base md:text-lg leading-relaxed">
              Minimum totals keep travel reasonable for us without charging a surprise fee.
              Nothing hidden — you know the floor before we show up.
            </p>
          </div>

          <div className="mt-10 grid md:grid-cols-2 gap-5">
            <PolicyCard
              label="Salisbury residents"
              value={BOOKING_POLICY.salisburyMinimum}
              desc="No minimum booking amount. Any size job is welcome."
              highlight
              testId="policy-salisbury"
            />
            <PolicyCard
              label={`${BOOKING_POLICY.radiusMiles}+ miles from Salisbury`}
              value={BOOKING_POLICY.outOfTownMinimum}
              desc="Minimum total booking amount. This is a project minimum — not an additional travel fee."
              testId="policy-outoftown"
            />
          </div>

          <div className="mt-6 flex items-start gap-3 rounded-xl bg-[#F8FAFC] border border-slate-200 p-4 md:p-5">
            <Info className="h-5 w-5 text-[#F97316] flex-shrink-0 mt-0.5" />
            <p className="text-sm text-slate-700 leading-relaxed">
              <span className="font-semibold text-[#0A192F]">Estimate visit fees are separate</span>{" "}
              from the booking minimum and are confirmed up front — call for a current quote.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function PolicyCard({ label, value, desc, highlight, testId }) {
  return (
    <div
      data-testid={testId}
      className={`relative rounded-2xl p-7 border transition-all ${
        highlight
          ? "bg-gradient-to-br from-[#F97316] to-[#EA580C] border-[#F97316] text-white shadow-[0_20px_50px_-20px_rgba(249,115,22,0.6)]"
          : "bg-white border-slate-200 text-[#0A192F]"
      }`}
    >
      {highlight && (
        <span className="absolute -top-3 right-6 inline-flex items-center gap-1 rounded-full bg-[#0A192F] text-white px-3 py-1 text-[10px] font-bold uppercase tracking-widest">
          <CircleCheck className="h-3 w-3" />
          Home Base
        </span>
      )}

      <div className="flex items-center gap-2.5">
        <div
          className={`h-9 w-9 rounded-lg grid place-items-center ${
            highlight ? "bg-white/20" : "bg-[#F97316]/10"
          }`}
        >
          <DollarSign
            className={`h-4 w-4 ${highlight ? "text-white" : "text-[#F97316]"}`}
          />
        </div>
        <div className={`text-sm font-semibold ${highlight ? "text-white" : "text-[#0A192F]"}`}>
          {label}
        </div>
      </div>

      <div className="mt-6 flex items-baseline gap-2">
        <div
          className={`font-display font-bold text-5xl md:text-6xl leading-none ${
            highlight ? "text-white" : "text-[#F97316]"
          }`}
        >
          {value}
        </div>
        <div className={`text-sm ${highlight ? "text-white/80" : "text-slate-500"}`}>
          minimum
        </div>
      </div>

      <p
        className={`mt-5 text-sm leading-relaxed ${
          highlight ? "text-white/90" : "text-slate-600"
        }`}
      >
        {desc}
      </p>
    </div>
  );
}

function CityGroup({ label, cities }) {
  return (
    <div className="rounded-2xl bg-white/[0.05] border border-white/10 p-5">
      <div className="text-xs uppercase tracking-widest text-[#F97316] font-semibold">
        {label}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {cities.map((c) => (
          <span
            key={c}
            className="px-3 py-1.5 rounded-full bg-white/[0.06] border border-white/10 text-sm text-white/85"
          >
            {c}
          </span>
        ))}
        <span className="px-3 py-1.5 rounded-full text-sm text-white/60">
          + surrounding towns
        </span>
      </div>
    </div>
  );
}
